import React, { useState, useMemo, useCallback } from 'react';
import HISTORICAL_EXPIRATIONS from '../data/HISTORICAL_DATA_ENHANCED.js';
import {
  generateInsights,
  predictNextWeek,
  recommendStrategy
} from '../engines/AIInsightsEngine.js';
import ThemeToggle from './ThemeToggle.jsx';
import BacktestingTab from './BacktestingTab.jsx';
import PatternsTab from './PatternsTab.jsx';
import ComparisonTab from './ComparisonTab.jsx';
import AIInsightsTabV2 from './AIInsightsTabV2.jsx';

function TA35SimulatorV3() {
  const [activeTab, setActiveTab] = useState('overview');
  const [theme, setTheme] = useState('dark');
  
  // Filters state
  const [filters, setFilters] = useState({
    expirationType: 'all',
    dateRange: 'all',
    volatility: 'all',
    direction: 'all',
    selectedMonth: 'all'
  });
  
  // Strategy state
  const [selectedExpiration, setSelectedExpiration] = useState(HISTORICAL_EXPIRATIONS[0].date);
  const [legs, setLegs] = useState([]);
  const [nextLegId, setNextLegId] = useState(1);
  
  // Toggle theme
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    document.body.className = theme === 'dark' ? 'light-theme' : 'dark-theme';
  };
  
  // Filtered expirations
  const filteredExpirations = useMemo(() => {
    let filtered = [...HISTORICAL_EXPIRATIONS];
    
    if (filters.expirationType === 'weekly') {
      filtered = filtered.filter(e => e.type === 'שבועי');
    } else if (filters.expirationType === 'monthly') {
      filtered = filtered.filter(e => e.type === 'חודשי');
    }
    
    if (filters.dateRange !== 'all') {
      const now = new Date(filtered[0].date);
      const cutoffMonths = filters.dateRange === 'year' ? 12 : filters.dateRange === '6months' ? 6 : 3;
      const cutoffDate = new Date(now);
      cutoffDate.setMonth(cutoffDate.getMonth() - cutoffMonths);
      filtered = filtered.filter(e => new Date(e.date) >= cutoffDate);
    }
    
    if (filters.selectedMonth !== 'all') {
      filtered = filtered.filter(e => e.month === filters.selectedMonth);
    }
    
    if (filters.direction !== 'all') {
      filtered = filtered.filter((exp, index) => {
        if (index === filtered.length - 1) return true;
        const change = exp.price - filtered[index + 1].price;
        if (filters.direction === 'positive') return change > 0;
        if (filters.direction === 'negative') return change < 0;
        return true;
      });
    }
    
    if (filters.volatility !== 'all') {
      const allChanges = [];
      for (let i = 0; i < HISTORICAL_EXPIRATIONS.length - 1; i++) {
        const change = Math.abs((HISTORICAL_EXPIRATIONS[i].price - HISTORICAL_EXPIRATIONS[i+1].price) / HISTORICAL_EXPIRATIONS[i+1].price * 100);
        allChanges.push(change);
      }
      const avgChange = allChanges.reduce((a, b) => a + b, 0) / allChanges.length;
      
      filtered = filtered.filter((exp, index) => {
        if (index === filtered.length - 1) return true;
        const change = Math.abs((exp.price - filtered[index + 1].price) / filtered[index + 1].price * 100);
        if (filters.volatility === 'high') return change > avgChange;
        if (filters.volatility === 'low') return change <= avgChange;
        return true;
      });
    }
    
    return filtered;
  }, [filters]);
  
  // Statistics
  const statistics = useMemo(() => {
    const enrichedData = filteredExpirations.map((exp, index) => {
      if (index === filteredExpirations.length - 1) {
        return { ...exp, weekChange: 0, weekChangePercent: 0 };
      }
      
      const prevWeek = filteredExpirations[index + 1];
      const weekChange = exp.price - prevWeek.price;
      const weekChangePercent = (weekChange / prevWeek.price) * 100;
      
      return { ...exp, weekChange, weekChangePercent };
    });
    
    const weekChanges = enrichedData.map(d => d.weekChange).filter(c => c !== 0);
    const weekChangePercents = enrichedData.map(d => d.weekChangePercent).filter(c => c !== 0);
    
    return {
      enrichedData,
      avgWeekChange: weekChanges.reduce((a, b) => a + b, 0) / weekChanges.length,
      avgWeekChangePercent: weekChangePercents.reduce((a, b) => a + b, 0) / weekChangePercents.length,
      maxWeekChange: Math.max(...weekChanges),
      minWeekChange: Math.min(...weekChanges),
      maxPrice: Math.max(...filteredExpirations.map(d => d.price)),
      minPrice: Math.min(...filteredExpirations.map(d => d.price)),
      totalExpirations: filteredExpirations.length,
      weeklyCount: filteredExpirations.filter(e => e.type === 'שבועי').length,
      monthlyCount: filteredExpirations.filter(e => e.type === 'חודשי').length,
    };
  }, [filteredExpirations]);
  
  // AI Insights
  const aiInsights = useMemo(() => {
    return generateInsights(filteredExpirations);
  }, [filteredExpirations]);
  
  // Strategy
  const selectedExp = useMemo(() => {
    return filteredExpirations.find(e => e.date === selectedExpiration);
  }, [selectedExpiration, filteredExpirations]);
  
  const strategyPnL = useMemo(() => {
    if (!selectedExp || legs.length === 0) return null;
    
    const spotPrice = selectedExp.price;
    const results = [];
    const minPrice = spotPrice * 0.90;
    const maxPrice = spotPrice * 1.10;
    const step = 5;
    
    for (let price = minPrice; price <= maxPrice; price += step) {
      let totalPnL = 0;
      
      for (const leg of legs) {
        let legValue = 0;
        if (leg.type === 'call') {
          legValue = Math.max(0, price - leg.strike);
        } else {
          legValue = Math.max(0, leg.strike - price);
        }
        
        const legPnL = leg.action === 'buy' 
          ? (legValue - leg.premium) * 50
          : (leg.premium - legValue) * 50;
        
        totalPnL += legPnL * leg.qty;
      }
      
      results.push({ price, pnl: totalPnL });
    }
    
    return {
      results,
      maxProfit: Math.max(...results.map(r => r.pnl)),
      maxLoss: Math.min(...results.map(r => r.pnl)),
      currentPrice: spotPrice,
    };
  }, [selectedExp, legs]);
  
  // Strategy management
  const addLeg = useCallback(() => {
    const newLeg = {
      id: nextLegId,
      type: 'call',
      action: 'buy',
      strike: selectedExp ? Math.round(selectedExp.price / 10) * 10 : 3650,
      premium: 5,
      qty: 1,
    };
    setLegs([...legs, newLeg]);
    setNextLegId(nextLegId + 1);
  }, [legs, nextLegId, selectedExp]);
  
  const removeLeg = useCallback((id) => {
    setLegs(legs.filter(leg => leg.id !== id));
  }, [legs]);
  
  const updateLeg = useCallback((id, field, value) => {
    setLegs(legs.map(leg => 
      leg.id === id ? { ...leg, [field]: value } : leg
    ));
  }, [legs]);
  
  const updateFilter = (key, value) => {
    if (key === 'reset') {
      setFilters({
        expirationType: 'all',
        dateRange: 'all',
        volatility: 'all',
        direction: 'all',
        selectedMonth: 'all'
      });
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };
  
  return (
    <div className={`ta35-simulator-v3 ${theme}`} dir="rtl">
      {/* Header */}
      <div className="sim-header-v3">
        <div className="sim-title-section">
          <h1>🚀 ASM INVESTMENT</h1>
          <p className="sim-subtitle-v3">
            AI SIMULATOR
          </p>
        </div>
        
        <div className="header-controls">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          
          <div className="sim-stats-quick-v3">
            <div className="stat-quick">
              <span className="stat-label">פקיעות</span>
              <span className="stat-value">{statistics.totalExpirations}</span>
            </div>
            <div className="stat-quick">
              <span className="stat-label">מחיר נוכחי</span>
              <span className="stat-value">{statistics.maxPrice.toFixed(1)}</span>
            </div>
            <div className="stat-quick">
              <span className="stat-label">ממוצע</span>
              <span className={`stat-value ${statistics.avgWeekChangePercent >= 0 ? 'positive' : 'negative'}`}>
                {statistics.avgWeekChangePercent >= 0 ? '+' : ''}{statistics.avgWeekChangePercent.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters Bar */}
      <FiltersBar filters={filters} onUpdateFilter={updateFilter} />
      
      {/* Tabs */}
      <div className="sim-tabs-v3">
        <button
          className={`sim-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 סקירה
        </button>
        <button
          className={`sim-tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          🤖 AI Insights
        </button>
        <button
          className={`sim-tab ${activeTab === 'patterns' ? 'active' : ''}`}
          onClick={() => setActiveTab('patterns')}
        >
          🔍 דפוסים
        </button>
        <button
          className={`sim-tab ${activeTab === 'backtesting' ? 'active' : ''}`}
          onClick={() => setActiveTab('backtesting')}
        >
          📈 Backtesting
        </button>
        <button
          className={`sim-tab ${activeTab === 'comparison' ? 'active' : ''}`}
          onClick={() => setActiveTab('comparison')}
        >
          ⚖️ השוואה
        </button>
        <button
          className={`sim-tab ${activeTab === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          📉 ניתוח
        </button>
        <button
          className={`sim-tab ${activeTab === 'strategy' ? 'active' : ''}`}
          onClick={() => setActiveTab('strategy')}
        >
          🎯 אסטרטגיה
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="sim-content-v3">
        {activeTab === 'overview' && (
          <OverviewTab statistics={statistics} />
        )}
        
    {activeTab === 'insights' && (
  <AIInsightsTabV2 expirations={filteredExpirations} />
)}

        
        {activeTab === 'patterns' && (
          <PatternsTab expirations={filteredExpirations} />
        )}
        
        {activeTab === 'backtesting' && (
          <BacktestingTab expirations={filteredExpirations} />
        )}
        
        {activeTab === 'comparison' && (
          <ComparisonTab expirations={filteredExpirations} />
        )}
        
        {activeTab === 'analysis' && (
          <AnalysisTab statistics={statistics} />
        )}
        
        {activeTab === 'strategy' && (
          <StrategyTab
            expirations={filteredExpirations}
            selectedExpiration={selectedExpiration}
            setSelectedExpiration={setSelectedExpiration}
            legs={legs}
            addLeg={addLeg}
            removeLeg={removeLeg}
            updateLeg={updateLeg}
            selectedExp={selectedExp}
            strategyPnL={strategyPnL}
          />
        )}
      </div>
    </div>
  );
}


// FiltersBar (same as V2)
function FiltersBar({ filters, onUpdateFilter }) {
  const hebrewMonths = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                        'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  
  return (
    <div className="filters-bar">
      <div className="filters-title">🔍 סינון נתונים</div>
      
      <div className="filters-grid">
        <div className="filter-item">
          <label>סוג פקיעה</label>
          <select 
            value={filters.expirationType} 
            onChange={(e) => onUpdateFilter('expirationType', e.target.value)}
          >
            <option value="all">הכל</option>
            <option value="weekly">שבועיות</option>
            <option value="monthly">חודשיות</option>
          </select>
        </div>
        
        <div className="filter-item">
          <label>טווח זמן</label>
          <select 
            value={filters.dateRange} 
            onChange={(e) => onUpdateFilter('dateRange', e.target.value)}
          >
            <option value="all">כל התקופה</option>
            <option value="year">שנה אחרונה</option>
            <option value="6months">6 חודשים</option>
            <option value="3months">3 חודשים</option>
          </select>
        </div>
        
        <div className="filter-item">
          <label>תנודתיות</label>
          <select 
            value={filters.volatility} 
            onChange={(e) => onUpdateFilter('volatility', e.target.value)}
          >
            <option value="all">הכל</option>
            <option value="high">גבוהה</option>
            <option value="low">נמוכה</option>
          </select>
        </div>
        
        <div className="filter-item">
          <label>כיוון</label>
          <select 
            value={filters.direction} 
            onChange={(e) => onUpdateFilter('direction', e.target.value)}
          >
            <option value="all">הכל</option>
            <option value="positive">עליות</option>
            <option value="negative">ירידות</option>
          </select>
        </div>
        
        <div className="filter-item">
          <label>חודש</label>
          <select 
            value={filters.selectedMonth} 
            onChange={(e) => onUpdateFilter('selectedMonth', e.target.value)}
          >
            <option value="all">כל החודשים</option>
            {hebrewMonths.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
        </div>
        
        <button 
          className="btn-reset-filters"
          onClick={() => onUpdateFilter('reset', null)}
        >
          🔄 אפס
        </button>
      </div>
    </div>
  );
}

// OverviewTab (simplified from V2)
function OverviewTab({ statistics }) {
  return (
    <div className="overview-tab">
      <h2>טבלת פקיעות</h2>
      <p className="tab-description">
        {statistics.totalExpirations} פקיעות ({statistics.weeklyCount} שבועיות, {statistics.monthlyCount} חודשיות)
      </p>
      
      <div className="table-container">
        <table className="expirations-table">
          <thead>
            <tr>
              <th>תאריך</th>
              <th>סוג</th>
              <th>מחיר</th>
              <th>שינוי</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {statistics.enrichedData.slice(0, 50).map((exp, index) => (
              <tr key={exp.date} className={index === 0 ? 'latest' : ''}>
                <td>{exp.date}</td>
                <td><span className={`type-badge ${exp.type === 'שבועי' ? 'weekly' : 'monthly'}`}>
                  {exp.type === 'שבועי' ? '📅' : '📆'}
                </span></td>
                <td className="price-cell">{exp.price.toFixed(1)}</td>
                <td className={exp.weekChange >= 0 ? 'positive' : 'negative'}>
                  {exp.weekChange !== 0 && (exp.weekChange >= 0 ? '+' : '')}{exp.weekChange.toFixed(1)}
                </td>
                <td className={exp.weekChangePercent >= 0 ? 'positive' : 'negative'}>
                  {exp.weekChangePercent !== 0 && (exp.weekChangePercent >= 0 ? '+' : '')}{exp.weekChangePercent.toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="summary-stats">
        <h3>סטטיסטיקות</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-label">ממוצע שינוי</div>
            <div className={`stat-card-value ${statistics.avgWeekChangePercent >= 0 ? 'positive' : 'negative'}`}>
              {statistics.avgWeekChangePercent.toFixed(2)}%
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">מקסימום</div>
            <div className="stat-card-value positive">+{statistics.maxWeekChange.toFixed(1)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">מינימום</div>
            <div className="stat-card-value negative">{statistics.minWeekChange.toFixed(1)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">טווח</div>
            <div className="stat-card-value">{(statistics.maxPrice - statistics.minPrice).toFixed(1)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// AIInsightsTab, AnalysisTab, StrategyTab - קצר מאוד, רק השלד
function AIInsightsTab({ insights }) {
  return (
    <div className="ai-insights-tab">
      <h2>🤖 תובנות AI</h2>
      <div className="prediction-card-simple">
        <div>כיוון: <strong>{insights.prediction.trend}</strong></div>
        <div>טווח: <strong>{insights.prediction.likelyRange}</strong></div>
        <div>סבירות: <strong>{insights.prediction.probability}</strong></div>
      </div>
      
      {insights.strategies.length > 0 && (
        <div className="strategies-simple">
          <h3>המלצות</h3>
          {insights.strategies.map((s, i) => (
            <div key={i} className="strategy-simple">
              <strong>{s.name}</strong>: {s.reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalysisTab({ statistics }) {
  return (
    <div className="analysis-tab">
      <h2>ניתוח תנועות</h2>
      <div className="insights-grid">
        <div className="insight-card">
          <h4>חיוביים</h4>
          <p className="positive">
            {statistics.enrichedData.filter(d => d.weekChange > 0).length}
          </p>
        </div>
        <div className="insight-card">
          <h4>שליליים</h4>
          <p className="negative">
            {statistics.enrichedData.filter(d => d.weekChange < 0).length}
          </p>
        </div>
      </div>
    </div>
  );
}

function StrategyTab({ expirations, selectedExpiration, setSelectedExpiration, legs, addLeg, removeLeg, updateLeg, selectedExp, strategyPnL }) {
  return (
    <div className="strategy-tab">
      <h2>בניית אסטרטגיה</h2>
      <div className="strategy-simple">
        <select value={selectedExpiration} onChange={(e) => setSelectedExpiration(e.target.value)}>
          {expirations.slice(0, 20).map(exp => (
            <option key={exp.date} value={exp.date}>{exp.date} - {exp.price.toFixed(1)}</option>
          ))}
        </select>
        
        <button onClick={addLeg}>+ הוסף רגל</button>
        
        {legs.map((leg, i) => (
          <div key={leg.id} className="leg-simple">
            <select value={leg.type} onChange={(e) => updateLeg(leg.id, 'type', e.target.value)}>
              <option value="call">Call</option>
              <option value="put">Put</option>
            </select>
            <select value={leg.action} onChange={(e) => updateLeg(leg.id, 'action', e.target.value)}>
              <option value="buy">קנה</option>
              <option value="sell">מכור</option>
            </select>
            <input type="number" value={leg.strike} onChange={(e) => updateLeg(leg.id, 'strike', Number(e.target.value))} />
            <input type="number" value={leg.premium} onChange={(e) => updateLeg(leg.id, 'premium', Number(e.target.value))} step="0.5" />
            <button onClick={() => removeLeg(leg.id)}>×</button>
          </div>
        ))}
        
        {strategyPnL && (
          <div className="pnl-simple">
            <div>רווח מקס: <span className="positive">₪{strategyPnL.maxProfit.toFixed(0)}</span></div>
            <div>הפסד מקס: <span className="negative">₪{strategyPnL.maxLoss.toFixed(0)}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TA35SimulatorV3;
